import type { TFunction } from 'i18next';

// Operator attribution of a desk mark (#813): "marcado por X a las HH:MM". Every
// roster stamp (check-in, outcomes, condition, voucher redemption) carries who +
// name + when under its own field names; this is the ONE place that turns them
// into the tooltip copy, so the chips all read the same.

export interface OperatorStampLike {
  /** `urn:user:<id>` — absent on stamps written before attribution existed. */
  by?: string | null | undefined;
  /** Display name from the signed operator context; absent → short URN label. */
  byName?: string | null | undefined;
  at?: string | null | undefined;
}

/** `urn:user:auth0|abc123` → `auth0|abc123`; anything else → as is. The UI never
 *  resolves names from a directory — the name travels on the stamp itself. */
export function shortOperatorLabel(urn: string): string {
  return urn.replace(/^urn:user:/, '');
}

export function stampTime(at: string): string {
  return new Date(at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

/** Who the mark names: the display name, else the short URN, else "un operador". */
export function stampWho(stamp: OperatorStampLike, t: TFunction<'pnr'>): string {
  if (stamp.byName) return stamp.byName;
  if (stamp.by) return shortOperatorLabel(stamp.by);
  return t('stamp.anOperator');
}

/**
 * Tooltip for a stamped chip. `undefined` when the stamp has no instant (nothing
 * to say). A passenger-made mark (`passenger: true`, e.g. a PASSENGER_REPLY
 * check-in) reads "confirmado por el pasajero"; an operator mark without an actor
 * (pre-#813) says so instead of inventing one.
 */
export function stampTitle(
  stamp: OperatorStampLike | undefined,
  t: TFunction<'pnr'>,
  opts: { passenger?: boolean } = {},
): string | undefined {
  if (!stamp?.at) return undefined;
  const time = stampTime(stamp.at);
  if (opts.passenger) return t('stamp.byPassenger', { time });
  if (!stamp.by && !stamp.byName) return t('stamp.markedAt', { time });
  return t('stamp.markedBy', { who: stampWho(stamp, t), time });
}
